/**
 * VerifyPropertyBrief — the editorial property brief that sits
 * above the verify-form on /verify.  This is the single highest-
 * leverage conversion moment in the funnel: the homeowner has
 * entered an address and now needs to feel "they really know my
 * home, and the numbers are telling me something" before they
 * commit to filling in property facts + email.
 *
 * The brief is one unified document with six sections:
 *
 *   1. Masthead — "Property Brief · 2026 Appraisal Roll"
 *   2. Hero — subject address + oversized appraised value + delta
 *   3. Tier summary — Neighborhood / ZIP / County median tiles
 *   4. Comparison ladder — You / Nbhd / ZIP / County stacked bars
 *   5. Trajectory chart — 5-year history + neighborhood + zip
 *      median overlays, large and readable (the user explicitly
 *      called out the prior chart as "too small and unreadable")
 *   6. Year-over-year distribution — how this zip's homes moved
 *      this year (decreased / flat / +5-10% / +10-20% / +20%+)
 *   7. Protest signal — sale-to-appraisal ratio callout
 *   8. Trust footer — sources, parcel counts, refresh date
 *
 * Data sources (in parallel):
 *   - GET {selectedAddress.url}verify_context/ → subject identity,
 *     5-year history, headline change, recent sale, neighborhood
 *   - fetchZipStats(zip)   → zip aggregates with multi-year history
 *   - fetchCountyStats(slug) → county aggregates
 *
 * Each block degrades gracefully — if zip 404s, the brief still
 * renders with just neighborhood+county tiers and a single-line
 * trajectory; if all aggregates fail, just the subject's history.
 * If verify_context itself fails, the brief renders nothing and
 * the form below is unaffected.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Spinner } from "reactstrap";

import { fetchZipStats, fetchCountyStats } from "../../lib/aggregatesApi";
import { titleCase } from "../../utils";

import {
  BriefShell,
  Masthead,
  Hero,
  DeltaChip,
  Section,
  SectionLabel,
  SectionHeading,
  SectionSub,
  PowerStrip,
  PowerCell,
  LadderRows,
  LadderRow,
  ChartFrame,
  ChartHeader,
  ChartLegend,
  ChartTooltip,
  DistributionFrame,
  DistributionBar,
  DistributionSeg,
  DistributionKeyCell,
  SpreadGrid,
  SpreadCard,
  LandingPin,
  ProtestCallout,
  TrustFooter,
} from "./styled";

// =============================================================
// Color tokens — kept in sync with styled.js so SVG strokes
// match the swatch chips in the legend.
// =============================================================
const C = {
  coral: "#e38a66",
  coralDark: "#a85a3a",
  navy: "#102a43",
  slate: "#475569",
  slateMute: "#94a3b8",
  paper: "#fdfbf7",
  rule: "#e9dfd0",
  green: "#1d6b46",
  greenSoft: "#9bc7b1",
  purple: "#7a4dd7",
  purpleSoft: "#b5a7e3",
};

// =============================================================
// Formatters
// =============================================================
function fmtUSD(value, { compact = false } = {}) {
  if (value == null || Number.isNaN(Number(value))) return null;
  const n = Number(value);
  if (compact && n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (compact && n >= 1_000) return `$${Math.round(n / 1_000)}k`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
}

function fmtCount(value) {
  if (value == null || Number.isNaN(Number(value))) return null;
  return Number(value).toLocaleString("en-US");
}

function pctDelta(subject, benchmark) {
  if (
    typeof subject !== "number" ||
    typeof benchmark !== "number" ||
    benchmark <= 0
  ) {
    return null;
  }
  return ((subject - benchmark) / benchmark) * 100;
}

function formatRefreshDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function countyNameToSlug(name) {
  if (!name) return null;
  return String(name)
    .toLowerCase()
    .replace(/\s*county\s*$/i, "")
    .trim()
    .replace(/\s+/g, "-");
}

function formatStreet(selectedAddress) {
  if (!selectedAddress) return null;
  const street =
    selectedAddress.street_address ||
    selectedAddress.formatted_address ||
    selectedAddress.address ||
    null;
  return street ? titleCase(street) : null;
}

// =============================================================
// Sparkline — tiny inline trajectory line for KPI cells.
// Pure SVG, no interactivity.  Renders 80% area + line so the
// cell reads "this number has a direction" at a glance.
// =============================================================
function Sparkline({ values }) {
  if (!Array.isArray(values) || values.length < 2) return null;
  const W = 120;
  const H = 26;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = Math.max(1, max - min);
  const dx = W / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * dx;
    const y = H - ((v - min) / span) * H * 0.85 - H * 0.075;
    return [x, y];
  });
  const linePath = points
    .map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x.toFixed(1)} ${y.toFixed(1)}`)
    .join(" ");
  const areaPath =
    `M ${points[0][0].toFixed(1)} ${H} ` +
    points.map(([x, y]) => `L ${x.toFixed(1)} ${y.toFixed(1)}`).join(" ") +
    ` L ${points[points.length - 1][0].toFixed(1)} ${H} Z`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
      <path d={areaPath} fill="#e38a66" fillOpacity="0.22" />
      <path
        d={linePath}
        fill="none"
        stroke="#a85a3a"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx={points[points.length - 1][0]}
        cy={points[points.length - 1][1]}
        r="2.5"
        fill="#a85a3a"
      />
    </svg>
  );
}

// =============================================================
// TrajectoryChart — the big readable line chart.  Centerpiece
// of the brief: wide 1280×480 viewBox + bold axes + hover
// tooltip + neighborhood-median + zip-median overlays + per-
// point YoY callouts.  This is the "make the chart actually
// readable" chart the user asked for.
// =============================================================
const VB_W = 1280;
const VB_H = 540;
const PAD_LEFT = 110;
const PAD_RIGHT = 44;
const PAD_TOP = 64;
const PAD_BOTTOM = 92;

function TrajectoryChart({
  subjectHistory,
  neighborhoodMedian,
  zipHistory,
  currentValue,
  header,
}) {
  const [activeIdx, setActiveIdx] = useState(null);
  const moveTickingRef = useRef(false);

  // Subject points: [year, value]
  const points = useMemo(() => {
    if (!Array.isArray(subjectHistory)) return [];
    return subjectHistory
      .map((row) => [
        Number(row.year),
        Number(row.appraised_value || row.appraised || 0),
      ])
      .filter(([y, v]) => Number.isFinite(y) && Number.isFinite(v) && v > 0)
      .sort((a, b) => a[0] - b[0]);
  }, [subjectHistory]);

  // Zip history points: subset overlapping with subject years.
  const zipPoints = useMemo(() => {
    if (!zipHistory || typeof zipHistory !== "object") return [];
    return Object.entries(zipHistory)
      .map(([yr, v]) => [Number(yr), Number(v)])
      .filter(([y, v]) => Number.isFinite(y) && Number.isFinite(v) && v > 0)
      .sort((a, b) => a[0] - b[0]);
  }, [zipHistory]);

  // Approximate path length for draw-in animation.  Hook MUST sit
  // before the early-return below — Rules of Hooks: every render
  // has to call the same hooks in the same order, including the
  // case where ``points`` has too few entries to render anything.
  // We over-compute slightly when bailing out, but it's a single
  // tight loop over a 5-element array — cheap.
  const approxLen = useMemo(() => {
    if (points.length < 2) return 400;
    const minYr = points[0][0];
    const maxYr = points[points.length - 1][0];
    const xForLocal = (yr) => {
      const t = (yr - minYr) / Math.max(1, maxYr - minYr);
      return PAD_LEFT + t * (VB_W - PAD_LEFT - PAD_RIGHT);
    };
    const values = points.map((p) => p[1]);
    const minV = Math.min(...values);
    const maxV = Math.max(...values);
    const axisMin = Math.max(0, minV - (maxV - minV) * 0.18);
    const axisMax = maxV + (maxV - minV) * 0.14;
    const yForLocal = (v) => {
      const t = (v - axisMin) / Math.max(1, axisMax - axisMin);
      return VB_H - PAD_BOTTOM - t * (VB_H - PAD_TOP - PAD_BOTTOM);
    };
    let total = 0;
    for (let i = 1; i < points.length; i++) {
      const x1 = xForLocal(points[i - 1][0]);
      const y1 = yForLocal(points[i - 1][1]);
      const x2 = xForLocal(points[i][0]);
      const y2 = yForLocal(points[i][1]);
      total += Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
    }
    return Math.max(400, total);
  }, [points]);

  if (points.length < 2) return null;

  const years = points.map((p) => p[0]);
  const minYear = years[0];
  const maxYear = years[years.length - 1];

  // Constrain zip points to the subject's year span (so we don't
  // distort the X-axis to accommodate years before/after).
  const zipInRange = zipPoints.filter(([y]) => y >= minYear && y <= maxYear);

  const allValues = [
    ...points.map((p) => p[1]),
    ...zipInRange.map((p) => p[1]),
    typeof neighborhoodMedian === "number" ? neighborhoodMedian : 0,
  ].filter((v) => v > 0);

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueAxisMin = Math.max(0, minValue - (maxValue - minValue) * 0.18);
  const valueAxisMax = maxValue + (maxValue - minValue) * 0.14;

  const xFor = (yr) => {
    const t = (yr - minYear) / Math.max(1, maxYear - minYear);
    return PAD_LEFT + t * (VB_W - PAD_LEFT - PAD_RIGHT);
  };
  const yFor = (v) => {
    const t = (v - valueAxisMin) / Math.max(1, valueAxisMax - valueAxisMin);
    return VB_H - PAD_BOTTOM - t * (VB_H - PAD_TOP - PAD_BOTTOM);
  };

  const subjectPath = points
    .map(([yr, v], i) => `${i === 0 ? "M" : "L"} ${xFor(yr)} ${yFor(v)}`)
    .join(" ");
  const subjectArea =
    `M ${xFor(minYear)} ${VB_H - PAD_BOTTOM} ` +
    points.map(([yr, v]) => `L ${xFor(yr)} ${yFor(v)}`).join(" ") +
    ` L ${xFor(maxYear)} ${VB_H - PAD_BOTTOM} Z`;

  const zipPath =
    zipInRange.length >= 2
      ? zipInRange
          .map(([yr, v], i) => `${i === 0 ? "M" : "L"} ${xFor(yr)} ${yFor(v)}`)
          .join(" ")
      : null;

  const yTicks = Array.from({ length: 5 }, (_, i) => {
    const v = valueAxisMin + ((valueAxisMax - valueAxisMin) * i) / 4;
    return { value: v, y: yFor(v) };
  });

  function handleMove(e) {
    if (moveTickingRef.current) return;
    moveTickingRef.current = true;
    const target = e.currentTarget;
    // Support touch as well as mouse: on a touch device e.clientX is undefined,
    // so read the first touch point. Lets mobile users reach the per-year
    // read-out the chart previously exposed only on hover.
    const clientX =
      e.touches && e.touches.length ? e.touches[0].clientX : e.clientX;
    window.requestAnimationFrame(() => {
      moveTickingRef.current = false;
      if (!target) return;
      const rect = target.getBoundingClientRect();
      if (rect.width === 0) return;
      const relX = clientX - rect.left;
      const vbX = (relX / rect.width) * VB_W;
      let bestIdx = 0;
      let bestDist = Infinity;
      for (let i = 0; i < points.length; i++) {
        const px = xFor(points[i][0]);
        const d = Math.abs(px - vbX);
        if (d < bestDist) {
          bestDist = d;
          bestIdx = i;
        }
      }
      setActiveIdx(bestIdx);
    });
  }

  const active = activeIdx != null ? points[activeIdx] : null;
  const prevPoint =
    activeIdx != null && activeIdx > 0 ? points[activeIdx - 1] : null;
  const yoyPct =
    active && prevPoint && prevPoint[1] > 0
      ? ((active[1] - prevPoint[1]) / prevPoint[1]) * 100
      : null;

  return (
    <ChartFrame style={{ "--draw-length": `${Math.round(approxLen)}px` }}>
      {header}
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        role="img"
        aria-label={`Appraisal trajectory ${minYear} to ${maxYear}`}
        style={{ touchAction: "pan-y" }}
        onMouseMove={handleMove}
        onMouseLeave={() => setActiveIdx(null)}
        onTouchStart={handleMove}
        onTouchMove={handleMove}
      >
        {/* Y-axis grid + tick labels */}
        {yTicks.map((t, i) => (
          <g key={`y-${i}`}>
            <line
              x1={PAD_LEFT}
              x2={VB_W - PAD_RIGHT}
              y1={t.y}
              y2={t.y}
              stroke={C.rule}
              strokeWidth="1.5"
            />
            <text
              x={PAD_LEFT - 18}
              y={t.y + 9}
              textAnchor="end"
              fontSize="26"
              fontWeight="700"
              fill={C.slate}
              fontFamily="Inter, system-ui, sans-serif"
              fontVariantNumeric="tabular-nums"
            >
              {fmtUSD(t.value, { compact: true })}
            </text>
          </g>
        ))}

        {/* X-axis */}
        <line
          x1={PAD_LEFT}
          x2={VB_W - PAD_RIGHT}
          y1={VB_H - PAD_BOTTOM}
          y2={VB_H - PAD_BOTTOM}
          stroke={C.slateMute}
          strokeWidth="2"
        />
        {years.map((yr) => (
          <g key={`x-${yr}`}>
            <line
              x1={xFor(yr)}
              x2={xFor(yr)}
              y1={VB_H - PAD_BOTTOM}
              y2={VB_H - PAD_BOTTOM + 10}
              stroke={C.slateMute}
              strokeWidth="2"
            />
            <text
              x={xFor(yr)}
              y={VB_H - PAD_BOTTOM + 44}
              textAnchor="middle"
              fontSize="28"
              fontWeight="800"
              fill={C.navy}
              fontFamily="Inter, system-ui, sans-serif"
              fontVariantNumeric="tabular-nums"
            >
              {yr}
            </text>
          </g>
        ))}

        {/* Neighborhood-median benchmark line */}
        {typeof neighborhoodMedian === "number" &&
          neighborhoodMedian > 0 &&
          neighborhoodMedian < valueAxisMax && (
            <g>
              <line
                x1={PAD_LEFT}
                x2={VB_W - PAD_RIGHT}
                y1={yFor(neighborhoodMedian)}
                y2={yFor(neighborhoodMedian)}
                stroke={C.green}
                strokeWidth="4"
                strokeDasharray="11 9"
                opacity="0.85"
              />
              <text
                x={VB_W - PAD_RIGHT - 16}
                y={yFor(neighborhoodMedian) - 16}
                textAnchor="end"
                fontSize="22"
                fontWeight="800"
                fill={C.green}
                fontFamily="Inter, system-ui, sans-serif"
              >
                Neighborhood median ·{" "}
                {fmtUSD(neighborhoodMedian, { compact: true })}
              </text>
            </g>
          )}

        {/* Zip-median multi-year line */}
        {zipPath && (
          <g opacity="0.9">
            <path
              d={zipPath}
              fill="none"
              stroke={C.purple}
              strokeWidth="4"
              strokeDasharray="5 8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            {zipInRange.map(([yr, v]) => (
              <circle
                key={`zip-pt-${yr}`}
                cx={xFor(yr)}
                cy={yFor(v)}
                r="7"
                fill="#fff"
                stroke={C.purple}
                strokeWidth="3"
              />
            ))}
          </g>
        )}

        {/* Subject area fill + line */}
        <defs>
          <linearGradient id="subjectFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={C.coral} stopOpacity="0.32" />
            <stop offset="60%" stopColor={C.coral} stopOpacity="0.14" />
            <stop offset="100%" stopColor={C.coral} stopOpacity="0.02" />
          </linearGradient>
        </defs>
        <path d={subjectArea} fill="url(#subjectFill)" />
        <path
          className="draw"
          d={subjectPath}
          fill="none"
          stroke={C.coralDark}
          strokeWidth="6"
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Snap-to-nearest guide */}
        {active && (
          <line
            x1={xFor(active[0])}
            x2={xFor(active[0])}
            y1={PAD_TOP}
            y2={VB_H - PAD_BOTTOM}
            stroke={C.coralDark}
            strokeWidth="2"
            strokeDasharray="6 6"
            opacity="0.45"
          />
        )}

        {/* Subject points */}
        {points.map(([yr, v], i) => {
          const isActive = activeIdx === i;
          return (
            <circle
              key={`pt-${yr}`}
              cx={xFor(yr)}
              cy={yFor(v)}
              r={isActive ? 13 : 10}
              fill={isActive ? C.coralDark : "#fff"}
              stroke={C.coralDark}
              strokeWidth="4"
              style={{ transition: "r 140ms ease, fill 140ms ease" }}
            />
          );
        })}

        {/* Per-point YoY callouts (skip first; only label years
            where the value moved meaningfully). */}
        {points.map(([yr, v], i) => {
          if (i === 0) return null;
          const prev = points[i - 1][1];
          if (prev <= 0) return null;
          const yoy = ((v - prev) / prev) * 100;
          if (Math.abs(yoy) < 0.5) return null;
          const isLast = i === points.length - 1;
          const up = yoy >= 0;
          return (
            <text
              key={`yoy-${yr}`}
              x={xFor(yr)}
              y={yFor(v) - (isLast ? 42 : 28)}
              textAnchor="middle"
              fontSize="20"
              fontWeight="800"
              fill={up ? C.coralDark : C.green}
              fontFamily="Inter, system-ui, sans-serif"
              fontVariantNumeric="tabular-nums"
            >
              {up ? "+" : "−"}
              {Math.abs(yoy).toFixed(1)}%
            </text>
          );
        })}

        {/* Endpoint label */}
        <text
          x={xFor(maxYear)}
          y={yFor(currentValue || points[points.length - 1][1]) - 68}
          textAnchor="end"
          fontSize="30"
          fontWeight="800"
          fill={C.navy}
          fontFamily="Inter, system-ui, sans-serif"
          fontVariantNumeric="tabular-nums"
        >
          {fmtUSD(currentValue || points[points.length - 1][1])}
        </text>
      </svg>

      {active && (() => {
        const x = xFor(active[0]);
        const y = yFor(active[1]);
        const leftPct = Math.max(8, Math.min(92, (x / VB_W) * 100));
        const topPct = Math.max(10, (y / VB_H) * 100);
        return (
          <ChartTooltip style={{ left: `${leftPct}%`, top: `${topPct}%` }}>
            <span className="tip-year">{active[0]}</span>
            <span className="tip-value">{fmtUSD(active[1])}</span>
            {yoyPct != null && (
              <span className={`tip-yoy ${yoyPct >= 0 ? "up" : "down"}`}>
                {yoyPct >= 0 ? "▲" : "▼"} {Math.abs(yoyPct).toFixed(1)}% YoY
              </span>
            )}
          </ChartTooltip>
        );
      })()}

      <ChartLegend>
        <span className="item">
          <span className="swatch subject" />
          Your home
        </span>
        {typeof neighborhoodMedian === "number" && neighborhoodMedian > 0 && (
          <span className="item">
            <span className="swatch neighborhood" />
            Neighborhood median
          </span>
        )}
        {zipPath && (
          <span className="item">
            <span className="swatch zip" />
            ZIP median
          </span>
        )}
      </ChartLegend>
    </ChartFrame>
  );
}

// =============================================================
// Distribution segments — palette + display copy
// =============================================================
const DIST_SEGS = [
  {
    key: "decrease",
    label: "Decreased",
    meta: "Yours could too",
    color: "#1d6b46",
    fg: "#fff",
  },
  {
    key: "flat_neg1_to_5",
    label: "Flat to +5%",
    meta: "Within market range",
    color: "#9bc7b1",
    fg: "#0e3a23",
  },
  {
    key: "rise_5_to_10",
    label: "+5% to +10%",
    meta: "Moderate rise",
    color: "#f5d0a9",
    fg: "#7a4a18",
  },
  {
    key: "rise_10_to_20",
    label: "+10% to +20%",
    meta: "Aggressive rise",
    color: "#f2a172",
    fg: "#5a2810",
  },
  {
    key: "rise_over_20",
    label: "+20% or more",
    meta: "Eligible for protest",
    color: "#c2502a",
    fg: "#fff",
  },
];

function DistributionView({ buckets, scope }) {
  if (!buckets || typeof buckets !== "object") return null;
  const total = DIST_SEGS.reduce((sum, s) => sum + (buckets[s.key] || 0), 0);
  if (total <= 0) return null;

  return (
    <DistributionFrame>
      <DistributionBar role="img" aria-label="Year-over-year distribution">
        {DIST_SEGS.map((s) => {
          const pct = Number(buckets[s.key]) || 0;
          if (pct <= 0) return null;
          return (
            <DistributionSeg
              key={s.key}
              $pct={pct}
              $color={s.color}
              $fg={s.fg}
              title={`${s.label}: ${pct.toFixed(1)}% of ${scope}`}
            >
              {pct >= 6 && (
                <span className="seg-pct">{pct.toFixed(0)}%</span>
              )}
            </DistributionSeg>
          );
        })}
      </DistributionBar>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
          gap: "0.4rem",
          marginTop: "0.75rem",
        }}
      >
        {DIST_SEGS.map((s) => {
          const pct = Number(buckets[s.key]) || 0;
          return (
            <DistributionKeyCell key={s.key} $color={s.color}>
              <span className="key-name">{s.label}</span>
              <span className="key-meta">
                {pct > 0 ? `${pct.toFixed(1)}%` : "—"} · {s.meta}
              </span>
            </DistributionKeyCell>
          );
        })}
      </div>
    </DistributionFrame>
  );
}

// =============================================================
// Main component
// =============================================================
const VerifyPropertyBrief = ({ selectedAddress }) => {
  const [verifyCtx, setVerifyCtx] = useState(null);
  const [zipStats, setZipStats] = useState(null);
  const [countyStats, setCountyStats] = useState(null);
  const [ctxError, setCtxError] = useState(null);

  // verify_context fetch — uses the property URL the address
  // search already returned, same as PropertyDossier on /reward.
  useEffect(() => {
    if (!selectedAddress || !selectedAddress.url) return;
    let cancelled = false;
    axios
      .get(`${selectedAddress.url}verify_context/`)
      .then((res) => {
        if (!cancelled) setVerifyCtx(res.data);
      })
      .catch((err) => {
        if (!cancelled) setCtxError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedAddress]);

  // Chain zip + county fetches once we know the slugs.  Each is
  // silent on failure — the brief degrades to whichever tiers
  // resolved.
  useEffect(() => {
    if (!verifyCtx || !verifyCtx.subject) return;
    let cancelled = false;
    const { zip_code, county } = verifyCtx.subject;
    if (zip_code) {
      fetchZipStats(zip_code)
        .then((data) => {
          if (!cancelled) setZipStats(data);
        })
        .catch(() => {});
    }
    const slug = countyNameToSlug(county);
    if (slug) {
      fetchCountyStats(slug)
        .then((data) => {
          if (!cancelled) setCountyStats(data);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
    };
  }, [verifyCtx]);

  // While the property snapshot is still loading, hold the space with a
  // contained loader instead of rendering nothing — the brief sits directly
  // above the verify form on the primary conversion screen, so a blank gap
  // reads as a broken layout. Errors and genuinely-empty data still bail.
  const isLoadingBrief =
    selectedAddress && selectedAddress.url && !verifyCtx && !ctxError;
  if (isLoadingBrief) {
    return (
      <BriefShell aria-label="Loading your property snapshot" aria-busy="true">
        <div
          role="status"
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "12px",
            padding: "48px 0",
            color: "#5C6472",
          }}
        >
          <Spinner />
          <span>Loading your property snapshot…</span>
        </div>
      </BriefShell>
    );
  }

  if (ctxError || !verifyCtx || !verifyCtx.subject) return null;

  const { subject, assessment_history, headline_change, recent_sale, neighborhood, tax_year } =
    verifyCtx;
  const subjectValue = subject.current_appraised_value;
  const street = formatStreet(selectedAddress);

  // Defensive: if we genuinely have nothing useful to render
  // (no value, no history, no aggregates), bail.
  const hasAnyData =
    typeof subjectValue === "number" ||
    (Array.isArray(assessment_history) && assessment_history.length > 0) ||
    neighborhood ||
    zipStats ||
    countyStats;
  if (!hasAnyData) return null;

  // ---- Tier summary ----
  const tiers = [];
  if (neighborhood && typeof neighborhood.median_appraised_current === "number") {
    tiers.push({
      key: "neighborhood",
      tone: "neighborhood",
      label: "Neighborhood",
      value: neighborhood.median_appraised_current,
      caption:
        typeof neighborhood.parcel_count === "number"
          ? `Median across ${fmtCount(neighborhood.parcel_count)} nearby ${
              neighborhood.parcel_count === 1 ? "home" : "homes"
            }`
          : "Median nearby home",
      yoy: neighborhood.median_yoy_change_pct,
    });
  }
  if (zipStats && typeof zipStats.median_appraised_current === "number") {
    tiers.push({
      key: "zip",
      tone: "zip",
      label: `ZIP ${subject.zip_code || ""}`.trim(),
      value: zipStats.median_appraised_current,
      caption:
        typeof zipStats.parcel_count_residential === "number"
          ? `Median across ${fmtCount(zipStats.parcel_count_residential)} homes in ${
              subject.zip_code || "this zip"
            }`
          : "Median across this zip",
      yoy: zipStats.median_yoy_change_pct,
    });
  }
  if (countyStats && typeof countyStats.median_appraised_current === "number") {
    tiers.push({
      key: "county",
      tone: "county",
      label: `${subject.county || "County"}`,
      value: countyStats.median_appraised_current,
      caption:
        typeof countyStats.parcel_count_residential === "number"
          ? `Median across ${fmtCount(countyStats.parcel_count_residential)} homes in ${
              subject.county || "the county"
            }`
          : "Median across the county",
      yoy: countyStats.median_yoy_change_pct,
    });
  }

  // ---- Comparison ladder rows ----
  const ladderRows = [];
  if (typeof subjectValue === "number") {
    ladderRows.push({
      key: "subject",
      name: "Your home",
      sub: street || "Current appraised value",
      value: subjectValue,
      subject: true,
      tone: "subject",
    });
  }
  tiers.forEach((t) => {
    ladderRows.push({
      key: t.key,
      name: t.label,
      sub: t.caption,
      value: t.value,
      tone: t.tone,
    });
  });

  const headlineDelta = headline_change && headline_change.delta_pct;
  const direction = headlineDelta != null && headlineDelta < 0 ? "down" : "up";

  // ---- Distribution buckets (prefer zip, fall back to county) ----
  const distBuckets =
    (zipStats && zipStats.yoy_change_buckets) ||
    (countyStats && countyStats.yoy_change_buckets) ||
    null;
  const distScope = zipStats
    ? `ZIP ${subject.zip_code || ""}`.trim()
    : countyStats
      ? `${subject.county || "your county"}`
      : null;

  // ---- Protest signal: sale-to-appraisal ratio ----
  // Use zip's value if available — closer-in geography is a
  // sharper signal.  Fall back to county.
  const sourceForRatio = zipStats || countyStats;
  const ratio =
    sourceForRatio && typeof sourceForRatio.sale_to_appraisal_ratio_median === "number"
      ? sourceForRatio.sale_to_appraisal_ratio_median
      : null;
  // Favorable for the protestor = sales BELOW appraisals (ratio < 1).
  // Stripe analogy: when the bank is paying less than the CAD says
  // the home is worth, the CAD is overvaluing → strong protest case.
  const favorable = ratio != null && ratio < 1;
  const ratioScope = zipStats
    ? `ZIP ${subject.zip_code || ""}`.trim()
    : `${subject.county || "your county"}`;

  // ---- Refresh date for trust footer ----
  const refresh =
    (zipStats && zipStats.last_updated) ||
    (countyStats && countyStats.last_updated) ||
    null;
  const refreshDate = formatRefreshDate(refresh);

  // Parcel-count line for the footer.
  const parcelLines = [];
  if (
    typeof neighborhood?.parcel_count === "number" &&
    neighborhood.parcel_count > 0
  ) {
    parcelLines.push(`${fmtCount(neighborhood.parcel_count)} neighborhood parcels`);
  }
  if (
    typeof zipStats?.parcel_count_residential === "number" &&
    zipStats.parcel_count_residential > 0
  ) {
    parcelLines.push(`${fmtCount(zipStats.parcel_count_residential)} ZIP parcels`);
  }
  if (
    typeof countyStats?.parcel_count_residential === "number" &&
    countyStats.parcel_count_residential > 0
  ) {
    parcelLines.push(`${fmtCount(countyStats.parcel_count_residential)} county parcels`);
  }

  // Scale denominator for the comparison ladder — bring the bars
  // close to (but not at) 100% so the visual reads as proportional
  // rather than capped.
  const trueMax = ladderRows.reduce((m, r) => Math.max(m, r.value || 0), 0);
  const ladderScale = trueMax * 1.12 || 1;

  // ---- Subject's YoY change (latest two history entries) ----
  // Used by both the hero's compare line and the YoY landing
  // bucket below the distribution.  Falls back to null when
  // history is too sparse.
  const subjectYoyPct = (() => {
    if (!Array.isArray(assessment_history) || assessment_history.length < 2) {
      return null;
    }
    const sorted = [...assessment_history].sort((a, b) => a.year - b.year);
    const last = sorted[sorted.length - 1];
    const prev = sorted[sorted.length - 2];
    const prevVal = Number(prev.appraised_value);
    const lastVal = Number(last.appraised_value);
    if (!Number.isFinite(prevVal) || !Number.isFinite(lastVal) || prevVal <= 0) {
      return null;
    }
    return ((lastVal - prevVal) / prevVal) * 100;
  })();

  // ---- Subject vs ZIP / Neighborhood median (for hero + power strip) ----
  const subjectVsZipPct =
    zipStats && typeof subjectValue === "number"
      ? pctDelta(subjectValue, zipStats.median_appraised_current)
      : null;
  const subjectVsNeighborhoodPct =
    neighborhood &&
    typeof subjectValue === "number" &&
    typeof neighborhood.median_appraised_current === "number"
      ? pctDelta(subjectValue, neighborhood.median_appraised_current)
      : null;

  // ---- Which YoY bucket does the subject fall in? ----
  // Threshold map matches the bucket key labels exactly.
  function bucketForYoy(yoy) {
    if (yoy == null) return null;
    if (yoy < -0.5) return "decrease";
    if (yoy < 5) return "flat_neg1_to_5";
    if (yoy < 10) return "rise_5_to_10";
    if (yoy < 20) return "rise_10_to_20";
    return "rise_over_20";
  }
  const subjectBucket = bucketForYoy(subjectYoyPct);
  const subjectBucketSeg = subjectBucket
    ? DIST_SEGS.find((s) => s.key === subjectBucket)
    : null;
  const subjectBucketPct =
    distBuckets && subjectBucket
      ? Number(distBuckets[subjectBucket]) || null
      : null;
  // Favorability of the user's landing bucket for protest pressure.
  // Decreased / flat = neutral (already low); rising 10%+ = high
  // pressure (warmer); over 20% = strongest protest case.
  const bucketTone =
    subjectBucket === "rise_over_20"
      ? "high"
      : subjectBucket === "rise_10_to_20"
        ? "high"
        : subjectBucket === "decrease"
          ? "favorable"
          : "neutral";

  // ---- Sparkline data for the 5-yr growth KPI cell ----
  const sparkSeries = Array.isArray(assessment_history)
    ? [...assessment_history]
        .sort((a, b) => a.year - b.year)
        .map((r) => Number(r.appraised_value))
        .filter((v) => Number.isFinite(v) && v > 0)
    : [];

  // ---- Power-strip cells (4 KPIs across the top) ----
  const powerCells = [];
  if (headlineDelta != null) {
    powerCells.push({
      key: "growth",
      tone: "growth",
      eyebrow: `Since ${headline_change.since_year}`,
      value: `${headlineDelta >= 0 ? "+" : "−"}${Math.abs(headlineDelta).toFixed(1)}`,
      unit: "%",
      direction: headlineDelta >= 0 ? "up" : "down",
      caption:
        typeof headline_change.delta_dollars === "number"
          ? `${headline_change.delta_dollars >= 0 ? "+" : "−"}${fmtUSD(
              Math.abs(headline_change.delta_dollars),
              { compact: true },
            )} on the appraisal roll`
          : `5-year cumulative change`,
      sparkSeries,
    });
  }
  if (subjectVsZipPct != null) {
    powerCells.push({
      key: "vsZip",
      tone: "compare",
      eyebrow: `vs ZIP ${subject.zip_code || ""}`.trim(),
      value: `${subjectVsZipPct >= 0 ? "+" : "−"}${Math.abs(subjectVsZipPct).toFixed(0)}`,
      unit: "%",
      direction: subjectVsZipPct >= 0 ? "up" : "down",
      caption: (
        <>
          {subjectVsZipPct >= 0 ? "Above" : "Below"} the median of{" "}
          <strong>{fmtUSD(zipStats.median_appraised_current, { compact: true })}</strong>{" "}
          across <strong>{fmtCount(zipStats.parcel_count_residential)}</strong> ZIP homes
        </>
      ),
    });
  } else if (subjectVsNeighborhoodPct != null) {
    powerCells.push({
      key: "vsNbhd",
      tone: "compare",
      eyebrow: `vs neighborhood`,
      value: `${subjectVsNeighborhoodPct >= 0 ? "+" : "−"}${Math.abs(subjectVsNeighborhoodPct).toFixed(0)}`,
      unit: "%",
      direction: subjectVsNeighborhoodPct >= 0 ? "up" : "down",
      caption: (
        <>
          {subjectVsNeighborhoodPct >= 0 ? "Above" : "Below"} the{" "}
          <strong>{fmtUSD(neighborhood.median_appraised_current, { compact: true })}</strong>{" "}
          neighborhood median
        </>
      ),
    });
  }
  if (ratio != null) {
    powerCells.push({
      key: "ratio",
      tone: "ratio",
      eyebrow: `Sale ÷ appraisal`,
      value: ratio.toFixed(2),
      unit: "×",
      caption: favorable ? (
        <>
          Recent sales in <strong>{ratioScope}</strong> closed{" "}
          <strong>below</strong> appraised value
        </>
      ) : (
        <>
          Recent sales in <strong>{ratioScope}</strong> closed near appraised
          value
        </>
      ),
    });
  }
  if (zipStats && typeof zipStats.parcel_count_residential === "number") {
    powerCells.push({
      key: "scale",
      tone: "scale",
      eyebrow: `Comparison set`,
      value: fmtCount(zipStats.parcel_count_residential),
      unit: "homes",
      caption: (
        <>
          Residential parcels in <strong>ZIP {subject.zip_code}</strong> we
          drew comparisons from
        </>
      ),
    });
  } else if (
    countyStats &&
    typeof countyStats.parcel_count_residential === "number"
  ) {
    powerCells.push({
      key: "scale",
      tone: "scale",
      eyebrow: `Comparison set`,
      value: fmtCount(countyStats.parcel_count_residential),
      unit: "homes",
      caption: (
        <>
          Residential parcels across <strong>{subject.county}</strong> we drew
          comparisons from
        </>
      ),
    });
  }

  return (
    <BriefShell aria-label="Your property brief">
      <Masthead>
        <span className="marque">Property Brief</span>
        <span className="roll">
          {(tax_year || new Date().getFullYear())} Appraisal Roll
        </span>
      </Masthead>

      <Hero>
        {street ? (
          <h2 className="subject-address">{street}</h2>
        ) : (
          <h2 className="subject-address">Your home in {subject.county || "Texas"}</h2>
        )}
        <p className="subject-area">
          {selectedAddress && selectedAddress.city ? (
            <>
              <strong>{titleCase(selectedAddress.city)}</strong>
              <span className="dot">·</span>
            </>
          ) : null}
          {subject.county ? (
            <>
              <strong>{subject.county}</strong>
              <span className="dot">·</span>
            </>
          ) : null}
          {subject.zip_code ? <>ZIP {subject.zip_code}</> : null}
        </p>

        <div className="value-stack">
          <span className="value">
            {fmtUSD(subjectValue) || "—"}
          </span>
          {headlineDelta != null && (
            <DeltaChip $direction={direction}>
              <span className="arrow">{direction === "up" ? "▲" : "▼"}</span>
              <span className="pct">
                {headlineDelta >= 0 ? "+" : ""}
                {headlineDelta.toFixed(1)}%
              </span>
              <span className="sep">·</span>
              <span className="qualifier">
                since {headline_change.since_year}
                {typeof headline_change.delta_dollars === "number" ? (
                  <>
                    {" · "}
                    {headline_change.delta_dollars >= 0 ? "+" : "−"}
                    {fmtUSD(Math.abs(headline_change.delta_dollars), {
                      compact: true,
                    })}
                  </>
                ) : null}
              </span>
            </DeltaChip>
          )}
          <span className="value-meta">
            {tax_year || new Date().getFullYear()} Appraised Value · Public records
          </span>
        </div>

        {/* Inline compare line beneath the value — positions the
            home immediately against its neighbors so the user gets
            the "where do I stand?" answer before scrolling. */}
        {(subjectVsZipPct != null || subjectVsNeighborhoodPct != null) && (
          <div className="hero-compare">
            <span>Your home reads</span>
            {subjectVsNeighborhoodPct != null && (
              <span
                className={`hc-pill ${
                  subjectVsNeighborhoodPct < 0 ? "below" : ""
                }`}
              >
                {subjectVsNeighborhoodPct >= 0 ? "▲" : "▼"}{" "}
                {Math.abs(subjectVsNeighborhoodPct).toFixed(0)}% vs
                neighborhood
              </span>
            )}
            {subjectVsZipPct != null && (
              <span
                className={`hc-pill ${subjectVsZipPct < 0 ? "below" : ""}`}
              >
                {subjectVsZipPct >= 0 ? "▲" : "▼"}{" "}
                {Math.abs(subjectVsZipPct).toFixed(0)}% vs ZIP{" "}
                {subject.zip_code || ""}
              </span>
            )}
            {ratio != null && (
              <>
                <span className="hc-divider" aria-hidden>·</span>
                <span>
                  area sales close at{" "}
                  <strong>{(ratio * 100).toFixed(0)}%</strong> of appraised
                </span>
              </>
            )}
          </div>
        )}
      </Hero>

      {/* === 1 · Power Strip (4 hero KPIs) === */}
      {powerCells.length > 0 && (
        <Section>
          <SectionLabel>
            <span className="stamp-number">1</span>
            <span className="stamp">The headline numbers</span>
            <span className="stamp-rule" />
          </SectionLabel>
          <PowerStrip aria-label="Headline statistics">
            {powerCells.map((c) => (
              <PowerCell key={c.key} $tone={c.tone} $direction={c.direction}>
                <span className="ps-eyebrow">{c.eyebrow}</span>
                <span className="ps-figure">
                  {c.direction && (
                    <span className="ps-arrow">
                      {c.direction === "up" ? "▲" : "▼"}
                    </span>
                  )}
                  <span className="ps-number">{c.value}</span>
                  {c.unit && <span className="ps-unit">{c.unit}</span>}
                </span>
                <span className="ps-caption">{c.caption}</span>
                {c.sparkSeries && c.sparkSeries.length >= 2 && (
                  <div className="ps-spark">
                    <Sparkline values={c.sparkSeries} />
                  </div>
                )}
              </PowerCell>
            ))}
          </PowerStrip>
        </Section>
      )}

      {/* === 2 · The big trajectory chart === */}
      {Array.isArray(assessment_history) && assessment_history.length >= 2 && (
        <Section>
          <SectionLabel>
            <span className="stamp-number">2</span>
            <span className="stamp">Your appraisal trajectory</span>
            <span className="stamp-rule" />
          </SectionLabel>
          <SectionHeading>
            Your home's appraisal history{" "}
            {zipStats && zipStats.median_appraised_history
              ? "versus the area"
              : ""}
          </SectionHeading>
          <SectionSub>
            Coral is your home's appraised value year over year.
            {neighborhood &&
              typeof neighborhood.median_appraised_current === "number" && (
                <>
                  {" "}The dashed green line is your neighborhood's median.
                </>
              )}
            {zipStats && zipStats.median_appraised_history && (
              <>
                {" "}The dotted purple line is ZIP{" "}
                {subject.zip_code || ""}'s median over the same window.
              </>
            )}{" "}
            Hover to read any year.
          </SectionSub>

          <TrajectoryChart
            subjectHistory={assessment_history}
            currentValue={subjectValue}
            neighborhoodMedian={
              neighborhood &&
              typeof neighborhood.median_appraised_current === "number"
                ? neighborhood.median_appraised_current
                : null
            }
            zipHistory={
              zipStats && zipStats.median_appraised_history
                ? zipStats.median_appraised_history
                : null
            }
            header={
              <ChartHeader>
                <div className="lhs">
                  <span className="label">
                    {tax_year || new Date().getFullYear()} Appraisal Trajectory
                  </span>
                  <span className="title">
                    {street || "Your home"}
                  </span>
                </div>
                {headlineDelta != null && (
                  <div className="rhs">
                    <span className="rhs-kpi">
                      {headlineDelta >= 0 ? "+" : "−"}
                      {Math.abs(headlineDelta).toFixed(1)}%
                    </span>
                    <span className="rhs-meta">
                      since {headline_change.since_year}
                    </span>
                  </div>
                )}
              </ChartHeader>
            }
          />
        </Section>
      )}

      {/* === 3 · Comparison + Distribution side-by-side spread === */}
      {(ladderRows.length >= 2 || distBuckets) && (
        <Section>
          <SectionLabel>
            <span className="stamp-number">3</span>
            <span className="stamp">Where your home sits</span>
            <span className="stamp-rule" />
          </SectionLabel>

          <SpreadGrid>
            {ladderRows.length >= 2 && (
              <SpreadCard>
                <div className="spread-head">
                  <span className="spread-eyebrow">The visual ladder</span>
                  <span className="spread-title">
                    Your home next to area medians
                  </span>
                  <span className="spread-sub">
                    Each bar is proportional to that group's typical
                    appraised value.
                  </span>
                </div>

                <LadderRows>
                  {ladderRows.map((r) => {
                    const widthPct = Math.max(
                      (r.value / ladderScale) * 100,
                      6,
                    );
                    const d = !r.subject ? pctDelta(subjectValue, r.value) : null;
                    const above = d != null && d > 2;
                    const below = d != null && d < -2;
                    return (
                      <LadderRow
                        key={r.key}
                        $subject={r.subject}
                        $tone={r.tone}
                      >
                        <div className="label">
                          <span className="name">{r.name}</span>
                          {r.sub && <span className="sub">{r.sub}</span>}
                        </div>
                        <div className="track">
                          <div
                            className="bar"
                            style={{ width: `${widthPct}%` }}
                          />
                        </div>
                        <div className="figure">
                          <span className="amount">{fmtUSD(r.value)}</span>
                          {d != null && (
                            <span
                              className={`delta ${
                                above ? "above" : below ? "below" : "even"
                              }`}
                            >
                              {above
                                ? `Yours +${Math.abs(d).toFixed(0)}%`
                                : below
                                  ? `Yours −${Math.abs(d).toFixed(0)}%`
                                  : "At the median"}
                            </span>
                          )}
                        </div>
                      </LadderRow>
                    );
                  })}
                </LadderRows>
              </SpreadCard>
            )}

            {distBuckets && (
              <SpreadCard>
                <div className="spread-head">
                  <span className="spread-eyebrow">
                    YoY change · {distScope}
                  </span>
                  <span className="spread-title">
                    How {distScope} appraisals moved this year
                  </span>
                  <span className="spread-sub">
                    Share of homes by year-over-year change. Coral on the
                    right = aggressive CAD increases = strongest protest
                    pressure.
                  </span>
                </div>
                <DistributionView buckets={distBuckets} scope={distScope} />

                {/* "Your home lands in this bucket" pin — converts
                    the distribution from a generic stat into a
                    you-are-here moment.  Only renders when we have
                    enough history to compute the user's YoY. */}
                {subjectBucketSeg && subjectBucketPct != null && (
                  <LandingPin $tone={bucketTone}>
                    <span className="lp-dot" aria-hidden />
                    <span className="lp-text">
                      Your home moved{" "}
                      <strong>
                        {subjectYoyPct >= 0 ? "+" : "−"}
                        {Math.abs(subjectYoyPct).toFixed(1)}%
                      </strong>{" "}
                      this year — that places it in the{" "}
                      <strong>{subjectBucketSeg.label}</strong> bucket along
                      with{" "}
                      <strong>{subjectBucketPct.toFixed(0)}%</strong> of homes
                      in {distScope}.
                    </span>
                  </LandingPin>
                )}
              </SpreadCard>
            )}
          </SpreadGrid>
        </Section>
      )}

      {ratio != null && (
        <Section>
          <SectionLabel>
            <span className="stamp-number">4</span>
            <span className="stamp">The protest signal</span>
            <span className="stamp-rule" />
          </SectionLabel>
          <ProtestCallout $favorable={favorable}>
            <div className="ratio-block">
              <span className="ratio-label">
                Sale-to-Appraisal · {ratioScope}
              </span>
              <span className="ratio-value">{ratio.toFixed(2)}×</span>
              <span className="ratio-sub">
                {sourceForRatio && typeof sourceForRatio.recent_sales_count === "number"
                  ? `${fmtCount(sourceForRatio.recent_sales_count)} sales · last ${
                      sourceForRatio.recent_sales_window_days || 365
                    } days`
                  : `last ${(sourceForRatio && sourceForRatio.recent_sales_window_days) || 365} days`}
              </span>
            </div>
            <p className="copy">
              {favorable ? (
                <>
                  Recent sales in <strong>{ratioScope}</strong> closed at
                  about <strong>{(ratio * 100).toFixed(0)}%</strong> of
                  appraised value — buyers are paying{" "}
                  <strong>less</strong> than the CAD says these homes are
                  worth.{" "}
                  {recent_sale && recent_sale.price ? (
                    <>
                      Your home's last on-record sale closed at{" "}
                      <strong>{fmtUSD(recent_sale.price)}</strong>
                      {recent_sale.date
                        ? ` (${new Date(recent_sale.date).getFullYear()})`
                        : ""}
                      .{" "}
                    </>
                  ) : null}
                  That gap is the strongest single signal that a protest can
                  win — it's exactly the case our reports build.
                </>
              ) : (
                <>
                  Recent sales in <strong>{ratioScope}</strong> closed at
                  about <strong>{(ratio * 100).toFixed(0)}%</strong> of
                  appraised value. Sales are tracking close to the appraisal
                  roll, so the protest case will rest on your property's
                  specific evidence — exactly what our report assembles.
                </>
              )}
            </p>
          </ProtestCallout>
        </Section>
      )}

      <TrustFooter>
        <span className="lhs">
          Sources: {subject.county || "County"} appraisal-district records
          {parcelLines.length > 0 ? ` · ${parcelLines.join(" · ")}` : ""}.
          Aggregates refresh quarterly; your home's values come from the
          latest appraisal roll on file.
        </span>
        <span className="rhs">
          {refreshDate
            ? `Updated ${refreshDate}`
            : `Tax year ${tax_year || new Date().getFullYear()}`}
        </span>
      </TrustFooter>
    </BriefShell>
  );
};

export default VerifyPropertyBrief;
